import { bouwTabelRegels, teltMeeInRangschikking } from './tabel';
import type { Korrel, Tijdvak } from './reeks';
import type { HandlingTimePerPersonRow } from './handlingTime.api';

/**
 * Story 33.3 — het rekenwerk onder de drie contextkaarten.
 *
 * Hier staat uitsluitend rekenwerk en geen opmaak, om dezelfde reden als bij `kerncijfers.ts`,
 * `tabel.ts`, `stapFilter.ts` en `aandachtslijst.ts`: de scherpste regels van deze story zijn
 * rekenregels — wie mag "de grootste bijdrager" heten, en wat is één tijdvak — en die horen op één
 * navraagbare plek te staan.
 *
 * De kaarten beantwoorden een ándere vraag dan de kerncijfers die ze vervangen: niet "wat is het
 * getal" maar "hoe hard is dit getal en waar komt het vandaan".
 */

/** Eén medewerker en zijn aandeel in de gemeten tijd. */
export interface BijdrageRegel {
  userId: number;
  /** Alleen aanwezig bij `named`. Nooit invullen met een verzonnen waarde. */
  userName?: string;
  userNameOutcome: string;
  minuten: number;
  /** Het aandeel in de TOTALE gemeten tijd, als percentage. */
  aandeel: number;
}

/**
 * Tijd die wél gemeten is maar aan niemand toe te wijzen valt, per reden opgeteld.
 *
 * Hij verdwijnt niet van de kaart: zonder deze regel zouden de staafjes samen minder dan het totaal
 * beslaan, en dan klopt er iets niet zonder dat er staat wat (AC-4a).
 */
export interface BuitenBeeld {
  userNameOutcome: string;
  minuten: number;
  aandeel: number;
}

/**
 * Hoe de tijd over de mensen ligt, in één woord — want dat woord bepaalt wat de kaart zegt.
 *
 * `geen-persoon` is nadrukkelijk iets anders dan `geen-meting`: er ís gemeten, alleen niet door
 * iemand die als mens aan te wijzen valt.
 */
export type VerdelingSoort =
  | 'geen-meting'
  | 'geen-persoon'
  | 'een-persoon'
  | 'gelijk'
  | 'gedeeld-hoogste'
  | 'overheersend'
  | 'verdeeld';

export interface Persoonsverdeling {
  soort: VerdelingSoort;
  /** Alle gemeten minuten, óók die van de regels die geen persoon zijn. `null` bij geen meting. */
  totaleMinuten: number | null;
  /** De mensen, van groot naar klein. */
  regels: BijdrageRegel[];
  /** Wat er buiten de mensen om gemeten is, per reden. */
  buitenBeeld: BuitenBeeld[];
  /**
   * De grootste bijdrager, of `null` zodra er niemand aan te wijzen valt.
   *
   * `null` bij één persoon, bij een gelijke verdeling én zodra de HOOGSTE BIJDRAGE GEDEELD is:
   * "100% komt van één persoon", "50% komt van één persoon" en "40% komt van Alice" terwijl Bob er
   * evenveel heeft, zijn alle drie waar en alle drie misleidend. De kaart zegt dan in woorden wat er
   * aan de hand is (AC-7, het randgeval van twee gelijk verdeelde mensen, en een
   * codereviewbevinding van 30 augustus 2026 over de gedeelde eerste plek).
   */
  grootste: BijdrageRegel | null;
  /**
   * Hoeveel REGELS de hoogste bijdrage delen. Eén bij een echte koploper, meer bij een gelijkspel.
   *
   * Dit getal beslist alleen of er een grootste bijdrager aan te wijzen is; het komt met opzet NIET
   * op het scherm. Eén regel is niet één mens (epic 31 §2), en een zin die regels telt om te zeggen
   * hoeveel mensen er gewerkt hebben zou precies dat doen.
   */
  aantalHoogste: number;
}

/**
 * Vanaf welk aandeel één persoon "ver boven de rest" heet.
 *
 * De helft: dan komt er meer tijd van die ene dan van alle anderen samen, en is een gemiddelde over
 * de groep vooral het gemiddelde van díe persoon (AC-6). Op productie stond dat getal op 62%.
 */
export const DREMPEL_OVERHEERSEND = 50;

/** Het aantal decimalen waarop een grens beslecht wordt, precies zoals in `normen.ts`. */
const PRECISIE = 1e6;

/** Afronden vóór een vergelijking, zodat drijvendekommaruis de grens niet verschuift. */
function opPrecisie(waarde: number): number {
  return Math.round(waarde * PRECISIE) / PRECISIE;
}

const LEGE_VERDELING: Persoonsverdeling = {
  soort: 'geen-meting',
  totaleMinuten: null,
  regels: [],
  buitenBeeld: [],
  grootste: null,
  aantalHoogste: 0,
};

/**
 * Hoe de gemeten tijd over de mensen ligt.
 *
 * De regels komen uit `bouwTabelRegels`, en dat is geen omweg maar de reden dat deze kaart en de
 * tabel niet uit elkaar kunnen lopen: het optellen per persoon gebeurt op één plek.
 *
 * `person-removed` en `automated-system` worden HIER al apart gezet, en niet pas bij het tonen.
 * `person-removed` vouwt een onbekend aantal vertrokken medewerkers samen tot één regel, en die
 * "de grootste bijdrager" noemen zou een groep als een mens presenteren; `automated-system` is de
 * dienst zelf. Dezelfde uitzondering als bij de rangschikking van story 31.6 en bij de
 * aandachtslijst van story 33.2, en met opzet dezelfde functie (AC-4a).
 *
 * De NOEMER blijft wél de volle gemeten tijd. Zou hij alleen over de mensen gaan, dan telden de
 * staafjes samen netjes op tot honderd procent terwijl er tijd buiten beeld staat — en dan liegt
 * een kaart die juist bedoeld is om te zeggen waar het getal vandaan komt.
 */
export function bouwPersoonsverdeling(rijen: HandlingTimePerPersonRow[]): Persoonsverdeling {
  const mensen: { userId: number; userName?: string; userNameOutcome: string; minuten: number }[] = [];
  const perReden = new Map<string, number>();
  let totaal = 0;

  for (const regel of bouwTabelRegels(rijen)) {
    const minuten = regel.totaalMinuten;
    // NIETS GEMETEN IS GEEN NUL, en een regel zonder minuten hoort dus nergens in deze kaart: een
    // staafje van nul naast een naam leest als "die deed niets" terwijl er over hem in deze stap
    // helemaal niets gemeten is.
    if (minuten === null || minuten <= 0) continue;
    totaal += minuten;

    if (teltMeeInRangschikking(regel)) {
      mensen.push({
        userId: regel.userId,
        ...(regel.userName !== undefined ? { userName: regel.userName } : {}),
        userNameOutcome: regel.userNameOutcome,
        minuten,
      });
      continue;
    }
    perReden.set(regel.userNameOutcome, (perReden.get(regel.userNameOutcome) ?? 0) + minuten);
  }

  if (totaal <= 0) return LEGE_VERDELING;

  const aandeelVan = (minuten: number): number => (minuten / totaal) * 100;

  // Van groot naar klein; bij een gelijk aantal minuten beslist het gebruikersnummer, zodat de
  // volgorde vast en herhaalbaar is (dezelfde regel als in de aandachtslijst).
  const regels: BijdrageRegel[] = mensen
    .map((mens) => ({ ...mens, aandeel: aandeelVan(mens.minuten) }))
    .sort((a, b) => (b.minuten - a.minuten) || (a.userId - b.userId));

  const buitenBeeld: BuitenBeeld[] = [...perReden.entries()]
    .map(([userNameOutcome, minuten]) => ({ userNameOutcome, minuten, aandeel: aandeelVan(minuten) }))
    .sort((a, b) => b.minuten - a.minuten);

  const eerste = regels[0];
  const hoogste = opPrecisie(eerste?.minuten ?? 0);
  // HOEVEEL MENSEN DE EERSTE PLEK DELEN. Afgerond vergeleken, zodat drijvendekommaruis niet twee
  // gelijke bijdragen uit elkaar trekt — dezelfde afronding als in `normen.ts`.
  const aantalHoogste = regels.filter((regel) => opPrecisie(regel.minuten) === hoogste).length;

  let soort: VerdelingSoort;
  if (eerste === undefined) soort = 'geen-persoon';
  else if (regels.length === 1) soort = 'een-persoon';
  else if (aantalHoogste === regels.length) soort = 'gelijk';
  // DE EERSTE PLEK IS GEDEELD, maar niet door iedereen. Dan is er geen grootste bijdrager, en de
  // volgorde hierboven (bij gelijke minuten beslist het gebruikersnummer) zou er willekeurig één
  // aanwijzen — codereviewbevinding 30 augustus 2026. Bij 40/40/20 stond er "40,0% komt van Alice"
  // terwijl Bob er evenveel droeg.
  else if (aantalHoogste > 1) soort = 'gedeeld-hoogste';
  else if (opPrecisie(eerste.aandeel) >= DREMPEL_OVERHEERSEND) soort = 'overheersend';
  else soort = 'verdeeld';

  return {
    soort,
    totaleMinuten: totaal,
    regels,
    buitenBeeld,
    grootste: soort === 'overheersend' || soort === 'verdeeld' ? (eerste ?? null) : null,
    aantalHoogste,
  };
}

/** Eén tijdvak zoals de kaart het tekent: gemeten of niet, en of het over de periodegrens steekt. */
export interface DekkingTijdvak {
  label: string;
  gemeten: boolean;
  rand: boolean;
}

/**
 * Hoeveel van de periode werkelijk een meting draagt.
 *
 * `soort` zegt wat dat betekent, en NIET in kleur: dekking is geen oordeel tegen een norm, en amber
 * en rood zijn op dit scherm voor het oordeel gereserveerd (AC-11).
 */
export interface Dekking {
  korrel: Korrel;
  totaal: number;
  gemeten: number;
  /** Hoeveel tijdvakken over de periodegrens steken; die zijn geen volle week of maand (AC-12b). */
  rand: number;
  soort: 'geen' | 'weinig' | 'ruim';
  tijdvakken: DekkingTijdvak[];
}

/**
 * De dekking uit de reeks die het scherm al ophaalt.
 *
 * GEEN EXTRA AANROEP en geen eigen kalender: de reeks vouwt de periode al uit tot tijdvakken en
 * markeert per tijdvak of er gemeten is (`Tijdvak.gemeten`). De eenheid is dus ook niet altijd de
 * dag — `korrelVoor` kiest dag tot en met 31 dagen, week daarboven en maand boven 183 dagen — en
 * die eenheid komt hier gewoon mee (AC-12 en AC-12a).
 *
 * De korrel is die waarom GEVRAAGD is, en dat is precies wat AC-12a voorschrijft: de eenheid volgt
 * `korrelVoor`. `bouwReeks` stempelt die korrel op elk tijdvak. Zou de bron ooit in een ándere
 * korrel antwoorden dan gevraagd, dan hangt `bouwReeks` die regels er als extra tijdvakken bij en
 * telt deze kaart er te veel; dat is bestaand gedrag van `reeks.ts` sinds story 31.3 — de grafiek
 * tekent ze dan net zo goed — en deze story verandert daar niets aan.
 *
 * `null` betekent "niet te bepalen" en nadrukkelijk niet "nul van dertig": zolang de reeks er niet
 * is, is er niets geteld. Nul zou lezen als "er is niets gemeten" terwijl er wel degelijk tijd
 * staat (AC-13).
 */
export function bouwDekking(tijdvakken: Tijdvak[] | null | undefined): Dekking | null {
  if (tijdvakken === null || tijdvakken === undefined || tijdvakken.length === 0) return null;

  const gemeten = tijdvakken.filter((tijdvak) => tijdvak.gemeten).length;
  const totaal = tijdvakken.length;

  return {
    // Alle tijdvakken van één reeks dragen dezelfde korrel; de eerste is dus de korrel van de reeks.
    korrel: tijdvakken[0]?.korrel ?? 'day',
    totaal,
    gemeten,
    rand: tijdvakken.filter((tijdvak) => tijdvak.rand).length,
    // MINDER DAN DE HELFT heet weinig: dan komt het totaal uit een minderheid van de periode, en
    // dan valt er nog geen norm op te baseren (AC-10). Geen enkele meting is een eigen geval — daar
    // valt niet "een deel" over te zeggen maar helemaal niets.
    soort: gemeten === 0 ? 'geen' : (gemeten * 2 < totaal ? 'weinig' : 'ruim'),
    tijdvakken: tijdvakken.map((tijdvak) => ({ label: tijdvak.label, gemeten: tijdvak.gemeten, rand: tijdvak.rand })),
  };
}
